<template>
  <div class="expense-report">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Expense Report</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ reportData.count || expenseItems.length }} Records
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem;" />
      <p class="mt-3 font-weight-bold">Loading expense report...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" @click="loadReportData" class="mt-3">Retry</b-button>
    </div>

    <div v-else class="report-page">
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div class="logo-wrapper mr-3" v-if="schoolLogo">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img">
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName }}<span v-if="campusBranch" class="text-muted"> - {{ campusBranch }}</span>
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">Expense Report</span>
              <span class="date-range-text">
                <strong>{{ accountLabel }}</strong> |
                <strong>{{ formatDate(dtFrom) }}</strong> to
                <strong>{{ formatDate(dtTo) }}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="table-container">
        <table class="ledger-style-table">
          <thead>
            <tr>
              <th class="text-center" style="width: 6%">V.NO</th>
              <th style="width: 9%">DATE</th>
              <th style="width: 7%">TYPE</th>
              <th style="width: 16%">ACCOUNT</th>
              <th style="width: 28%">DESCRIPTION</th>
              <th style="width: 9%">CHEQUE NO</th>
              <th class="text-right" style="width: 12%">AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in expenseItems" :key="`${item.journalEntryID}-${item.detailID}-${index}`">
              <td class="text-center">
                <a
                  v-if="item.journalEntryID"
                  href="#"
                  class="voucher-link no-print-link"
                  @click.prevent="openVoucher(item)"
                >
                  {{ item.voucherNum || "-" }}
                </a>
                <span v-else>{{ item.voucherNum || "-" }}</span>
              </td>
              <td>{{ formatDate(item.date) }}</td>
              <td>{{ item.voucherType || "-" }}</td>
              <td>
                <div>{{ item.accountTitle || "-" }}</div>
                <small v-if="item.accountCode" class="text-muted account-code">{{ item.accountCode }}</small>
              </td>
              <td>{{ item.description || item.detailDescription || "-" }}</td>
              <td>{{ item.chequeNo || "-" }}</td>
              <td class="text-right amount-cell font-weight-bold">
                {{ formatRowAmount(item) }}
              </td>
            </tr>
            <tr v-if="expenseItems.length === 0">
              <td colspan="7" class="text-center py-4 text-muted">
                No expense entries found for this period.
              </td>
            </tr>
            <tr v-if="expenseItems.length > 0" class="total-row">
              <td colspan="6" class="text-right font-weight-bold">TOTAL</td>
              <td class="text-right font-weight-bold">{{ formatCurrency(reportData.totalDebit) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "ExpenseReportPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportData: {
        totalDebit: 0,
        totalCredit: 0,
        totalExpense: 0,
        count: 0,
        data: [],
      },
      schoolLogo: null,
      campusName: "",
      campusBranch: "",
      db: "",
      cID: "",
      dtFrom: "",
      dtTo: "",
      accountID: 0,
      accountLabel: "All Accounts",
      reportDomain: (this.$store.state.report || "").replace(/_/g, ""),
    };
  },
  computed: {
    expenseItems() {
      return this.reportData.data || [];
    },
    currentDateTime() {
      const now = new Date();
      return now.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db;
      this.cID = query.cID || query.cId;
      this.accountID = query.accountID != null ? query.accountID : 0;
      this.dtFrom = query.dtFrom;
      this.dtTo = query.dtTo;
      this.accountLabel = query.accountTitle || "All Accounts";

      this.campusName =
        (this.$store.state.currentBranch &&
          this.$store.state.currentBranch.branch) ||
        "My Skool";
      this.schoolLogo =
        (this.$store.state.currentBranch &&
          this.$store.state.currentBranch.logo) ||
        null;

      if (!this.db || !this.dtFrom || !this.dtTo) {
        this.error = "Invalid parameters. Please close and try again.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const response = await axios.get(`${this.$store.state.domain}campuses`, {
          params: {
            db: this.db,
            cId: this.cID,
          },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          response.data.data &&
          response.data.data.length > 0
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          const info = match || response.data.data[0];
          this.campusName = info.name;
          this.campusBranch = this.cID == 0 ? "All Campuses" : info.branch;
          this.schoolLogo = info.logo;
        }
      } catch (e) {
        console.error("Failed to load campus info:", e);
      }
    },
    async loadReportData() {
      try {
        this.loading = true;
        this.error = null;

        let baseUrl = this.$store.state.domain;
        if (!baseUrl.endsWith("/")) {
          baseUrl += "/";
        }

        const response = await axios.get(`${baseUrl}JournalEntry/GetExpenseReport`, {
          params: {
            db: this.db,
            cID: this.cID,
            dtFrom: this.dtFrom,
            dtTo: this.dtTo,
            accountID: this.accountID || 0,
          },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (response.data && response.data.status === "success") {
          this.reportData = {
            totalDebit: response.data.totalDebit || 0,
            totalCredit: response.data.totalCredit || 0,
            totalExpense: response.data.totalExpense || 0,
            count: response.data.count || 0,
            data: response.data.data || [],
          };
        } else {
          this.error =
            response.data?.message || "Failed to load expense report data.";
        }
      } catch (error) {
        console.error("Failed to load expense report:", error);
        this.error = "Network error or server unavailable.";
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      } catch (e) {
        return dateString;
      }
    },
    formatCurrency(value) {
      if (value === undefined || value === null) return "0.00";
      return new Intl.NumberFormat("en-PK", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    },
    rowAmountValue(item) {
      const debit = Number(item.debit) || 0;
      const credit = Number(item.credit) || 0;
      const amount = Number(item.amount) || 0;
      if (debit > 0) return debit;
      if (credit > 0) return credit;
      if (amount > 0) return amount;
      return 0;
    },
    formatRowAmount(item) {
      const val = this.rowAmountValue(item);
      return val > 0 ? this.formatCurrency(val) : "-";
    },
    openVoucher(item) {
      const entryID = item.journalEntryID;
      if (!entryID) return;

      const baseUrl = `https://${this.reportDomain}.myskool.app`;
      const url = `${baseUrl}/Account/PrintEntryVoucher?entryID=${entryID}&dbb=${encodeURIComponent(
        this.db
      )}&cID=${this.cID}`;
      window.open(url, "_blank");
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.expense-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-page {
  background: #ffffff;
  padding: 3rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1400px;
  margin: 30px auto;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;

  .school-logo-img {
    height: 130px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 2rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }

    .date-range-text {
      color: #555;
    }
  }
}

.ledger-style-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  border: 1px solid #dee2e6;
  background: white;

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 10px 12px;
    box-sizing: border-box;
    vertical-align: top;
    font-size: 0.95rem;
  }

  thead {
    background: #2c3e50 !important;

    th {
      color: white !important;
      font-weight: 800;
      text-transform: uppercase;
      font-size: 0.85rem;
      letter-spacing: 0.5px;
    }
  }

  tbody tr:nth-child(even):not(.total-row) {
    background-color: #f8f9fa;
  }

  .voucher-link {
    color: #000;
    text-decoration: none;
    font-weight: 700;

    &:hover {
      text-decoration: underline;
      color: #2e86de;
    }
  }

  .total-row {
    background: #f1f3f5 !important;

    td {
      padding: 12px;
      font-weight: 800;
      border-top: 2px solid #2c3e50;
      color: #2c3e50;
    }
  }

  .amount-cell {
    color: #2d3436;
  }
}

.text-right {
  text-align: right;
}
.text-center {
  text-align: center;
}
.font-weight-bold {
  font-weight: 700;
}
.bg-light {
  background-color: #f8f9fa;
}

@page {
  size: A4 landscape;
  margin: 0;
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #000 !important;
  }

  .expense-report {
    background: white !important;
    padding: 0 !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 0.8cm 0.6cm !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .report-header-modern {
    margin-bottom: 12px !important;
    padding-bottom: 10px !important;
    border-bottom: 2px solid #000 !important;

    .school-logo-img {
      height: 70px !important;
      max-width: 180px !important;
      object-fit: contain !important;
    }

    .school-name-full {
      font-size: 1.25rem !important;
    }

    .report-info-line {
      font-size: 0.85rem !important;
    }
  }

  .table-container {
    width: 100% !important;
    overflow: visible !important;
  }

  .ledger-style-table {
    font-size: 7.5pt !important;
    width: 100% !important;
    page-break-inside: auto !important;

    th,
    td {
      padding: 3px 5px !important;
      border: 1px solid #000 !important;
      color: #000 !important;
      line-height: 1.2 !important;
    }

    thead {
      display: table-header-group !important;
    }

    thead th {
      background: #f1f2f6 !important;
      color: #000 !important;
      font-weight: 800 !important;
      font-size: 7pt !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    tbody tr.total-row {
      page-break-inside: avoid !important;
      background: #f8f9fa !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .account-code {
      display: none !important;
    }

    .voucher-link,
    .no-print-link {
      color: #000 !important;
      text-decoration: none !important;
    }
  }
}
</style>
